import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkspaceId } from 'balus-common';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { ViewModelEntity } from '@view-model/domain/view-model';
import type { RouteParams } from '@user/pages/RouteParams';
import { useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  controlAtom,
  setScrolledViewModelAtom,
  setScrolledWorkspaceAtom,
  sidebarTreeContainerRefAtom,
} from './SidebarTreeStore/sidebarTreeControlState';
import { openWorkspaceIdsAtom, toggleWorkspaceOpenAtom } from './SidebarTreeStore/sidebarTreeOpenState';
import { foldersAtom, viewModelsAtom } from './SidebarTreeStore/sidebarTreeRecordsState';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';

type Return = {
  firstLoaded: boolean;
  isOpen: boolean;
  isSelected: boolean;
  toggleWorkspace: () => void;
  sortedFolders: Folder[];
  sortedViewModels: ViewModelEntity[];
  resetScrollWorkspace: () => void;
  resetScrollViewModel: () => void;
  scrolledViewModelId: string | null;
  scrolledFolderId: string | null;
  sidebarTreeContainerRef: React.RefObject<HTMLDivElement> | null;
};

export const useSidebarWorkspace = (workspaceId: WorkspaceId, rootFolderTree: RootFolderTree): Return => {
  const folders = useAtomValue(foldersAtom);
  const viewModels = useAtomValue(viewModelsAtom);
  const sidebarTreeContainerRef = useAtomValue(sidebarTreeContainerRefAtom);

  const openWorkspaceIds = useAtomValue(openWorkspaceIdsAtom);
  const toggleWorkspaceOpen = useSetAtom(toggleWorkspaceOpenAtom);
  const { scrolledViewModelId, scrolledFolderId } = useAtomValue(controlAtom);
  const setScrolledWorkspace = useSetAtom(setScrolledWorkspaceAtom);
  const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);

  const { workspaceId: workspaceIdParam, folderId: folderIdParam } = useParams<RouteParams>();

  const [firstLoaded, setFirstLoaded] = useState(false);

  const isOpen = useMemo(() => !!openWorkspaceIds[workspaceId], [openWorkspaceIds, workspaceId]);

  // ワークスペース直下のフォルダ及びビューモデルが全て取得済みかをチェックする
  useEffect(() => {
    if (firstLoaded) return;

    const childrenFolderIds = rootFolderTree.getFolderIdsOfRoot();
    const childrenViewModelIds = rootFolderTree.getViewModelIdsOfRoot();
    const alreadyLoadedFolderIds = Object.keys(folders);
    const alreadyLoadedViewModelIds = Object.keys(viewModels);

    if (
      (childrenFolderIds.length <= 0 && childrenViewModelIds.length <= 0) ||
      (childrenFolderIds.every((id) => alreadyLoadedFolderIds.includes(id)) &&
        childrenViewModelIds.every((id) => alreadyLoadedViewModelIds.includes(id)))
    ) {
      setFirstLoaded(true);
    }
  }, [rootFolderTree, firstLoaded, folders, viewModels]);

  const toggleWorkspace = useCallback(() => {
    toggleWorkspaceOpen(workspaceId);
  }, [workspaceId, toggleWorkspaceOpen]);

  const sortedFolders = useMemo(
    () =>
      rootFolderTree
        .getFolderIdsOfRoot()
        .map((folderId) => folders[folderId])
        .filter((folder) => !!folder)
        .sort(Folder.compare),
    [rootFolderTree, folders]
  );

  const sortedViewModels = useMemo(
    () =>
      rootFolderTree
        .getViewModelIdsOfRoot()
        .map((viewModelId) => viewModels[viewModelId])
        .filter((viewModel) => !!viewModel)
        .sort(ViewModelEntity.compare),
    [rootFolderTree, viewModels]
  );

  const resetScrollWorkspace = useCallback(() => {
    setScrolledWorkspace('');
  }, [setScrolledWorkspace]);

  const resetScrollViewModel = useCallback(() => {
    setScrolledViewModel('');
  }, [setScrolledViewModel]);

  const isSelected = useMemo(
    () => !folderIdParam && workspaceId === workspaceIdParam,
    [workspaceId, workspaceIdParam, folderIdParam]
  );

  return {
    firstLoaded,
    isOpen,
    isSelected,
    toggleWorkspace,
    sortedFolders,
    sortedViewModels,
    resetScrollWorkspace,
    resetScrollViewModel,
    scrolledViewModelId,
    scrolledFolderId,
    sidebarTreeContainerRef,
  };
};
